import { ThunkDispatch, createAsyncThunk } from '@reduxjs/toolkit';

import { nifsAxios } from 'constants/axios';
import axios, { AxiosError } from 'axios';
import lodash from 'lodash';

import { matchSliceActions, selectAllPlayers, selectAllStaff } from 'features/match/matchSlice';

import { AppThunk } from 'store';

import generateRandomId from 'helpers/generateRandomId';
import calculatePeriod from 'helpers/calculatePeriod';
import eventAlreadyExists from 'helpers/eventAlreadyExists';
import calculateOvertime from 'helpers/calculateOvertime';
import isEqualMatchEvent from './helpers/isEqualMatchEvent';

import { PatchMatchProps } from './thunksTypes';
import { RootState } from 'rootReducer';
import findFirstEvent from 'helpers/findFirstEvent';
import { MatchScore } from './matchSliceTypes';

import { getMinutes, getSeconds } from './helpers/calculateTime';
import isGoalEvent from './helpers/isGoalEvent';
import { NetworkErrorMessage } from 'features/network/networkSliceTypes';
import getPeriodLengthFromMatchTime from '../../components/MatchWatch/helpers/getPeriodLengthFromMatchTime';
import { MatchStatus, noTimeMatchStatuses } from '../../enums/matchStatus';
import { TeamOption } from '../../enums/teamOption';
import MatchEvent from '../../interfaces/matchEvent';
import {
  importantMatchEventTypeIds,
  MatchEventType,
  matchStatusEvents,
  secondaryEvents,
  substitutionEvents,
  tertiaryEvents,
} from '../../enums/matchEventType';
import { MatchEventStatus } from '../../enums/matchEventStatus';
import matchEventPayloadTransformer from '../../helpers/matchEventPayloadTransformer';
import getCompactTeamFromTeamOption from '../input/helpers/getTeamFromTeamOption';
import findPersonInListFromPersonId from '../input/helpers/findPersonInListFromPersonId';
import findActiveKeeper from '../input/helpers/findActiveKeeper';
import compactPersonTransformer from './helpers/compactPersonTransformer';
import { Match } from '../../interfaces/match';
import { Selects } from '../input/inputSliceTypes';
import Period from '../../enums/period';
import moment from 'moment-timezone';
import getPeriodTimeFromMatchLength from '../../components/MatchWatch/helpers/getPeriodTimeFromMatchLength';
import getExtraTimePeriodLengthFromMatchLength from '../../components/MatchWatch/helpers/getExtraTimePeriodLengthFromMatchLength';
import getEventTimeWhenOvertime from '../../helpers/getEventTimeWhenOvertime';
import populateLiveFeedSerialIds from '../../helpers/populateLiveFeedSerialIds';
import MatchStatistic from '../../interfaces/matchStatistic';
import matchStatisticPayloadTransformer from '../../helpers/matchStatisticPayloadTransformer';
import { MatchStatisticStatus } from '../../enums/matchStatisticStatus';
import { timeSliceActions } from '../time/timeSlice';
import { MatchStatisticType } from '../../enums/matchStatisticType';
import hasPenaltiesShootoutStarted from '../../helpers/hasPenaltiesShootoutStarted';
import hasExtraTime from '../../helpers/hasExtraTime';
import ErrorCode from '../../enums/errorCode';
import { inputSliceActions } from '../input/inputSlice';
import { setActiveSelectValue } from '../input/thunks';
import { modalsActions } from '../modals/modalsSlice';
import { storeItemToLog } from '../log/storeItemToLog';
import { v4 as uuidv4 } from 'uuid';
import findPersonInListFromId from '../input/helpers/findPersonInListFromId';

export const makeMatchEvent = createAsyncThunk('match/makeMatchEventNew', (payload: any, thunkAPI) => {
  const rootState = thunkAPI.getState() as RootState;
  const matchState = rootState.matchState;
  const inputState = rootState.input;
  const timeState = rootState.timeState;
  const { match } = matchState;
  const { time } = timeState;
  let { activeEventTypeId: matchEventTypeId } = inputState;
  const { selects, team: teamOption } = inputState;
  let { comment } = inputState.inputs;
  let { eventTime } = inputState.inputs;
  const { indirectAssist, ownGoal, matchSummaryEvent } = inputState.checkboxes;
  const penaltiesShootoutStarted = hasPenaltiesShootoutStarted(match.matchEvents);

  const liveFeedInternalIds = populateLiveFeedSerialIds(matchState.match.matchEvents);

  if (payload.goalPenalty) {
    //So that the goal don't get the same time as the penalty event
    eventTime = null;
  }

  let matchEventMinutes = getMinutes(eventTime);
  let matchEventSeconds = getSeconds(eventTime);
  if (noTimeMatchStatuses.includes(match.matchStatusId)) {
    matchEventMinutes = null;
    matchEventSeconds = null;
  } else if (!eventTime) {
    matchEventMinutes = time.minutes;
    matchEventSeconds = time.seconds;
  }

  let overtime;
  if (matchEventMinutes) {
    overtime = calculateOvertime(matchEventMinutes, match.matchLength, match.matchStatusId);
    if (overtime) {
      if (match.matchStatusId === MatchStatus.FIRST_HALF) {
        matchEventMinutes = getPeriodLengthFromMatchTime(match.matchLength);
      } else if (match.matchStatusId === MatchStatus.SECOND_HALF) {
        matchEventMinutes = getPeriodLengthFromMatchTime(match.matchLength) * 2;
      } else if (match.matchStatusId === MatchStatus.FIRST_HALF_EXTRA_TIME) {
        matchEventMinutes =
          getPeriodLengthFromMatchTime(match.matchLength) * 2 +
          getExtraTimePeriodLengthFromMatchLength(match.matchLength);
      } else if (match.matchStatusId === MatchStatus.SECOND_HALF_EXTRA_TIME) {
        matchEventMinutes =
          getPeriodLengthFromMatchTime(match.matchLength) * 2 +
          getExtraTimePeriodLengthFromMatchLength(match.matchLength) * 2;
      }
    }
  }

  if (payload.matchEventTypeId) {
    matchEventTypeId = payload.matchEventTypeId;
  }

  if (matchEventTypeId === MatchEventType.COMMENT && matchSummaryEvent) {
    matchEventTypeId = MatchEventType.MATCH_SUMMARY;
  }

  const important = importantMatchEventTypeIds.includes(matchEventTypeId) || inputState.important;
  const liveFeedId = match.liveFeeds ? match.liveFeeds[0].id : undefined;

  const { team, person, isStaffEvent } = getTeamAndPerson(
    teamOption,
    match,
    matchEventTypeId,
    rootState,
    selects,
    true,
  );

  if (
    payload.matchEventTypeId &&
    payload.matchEventTypeId !== MatchEventType.PENALTY_MISS &&
    payload.matchEventTypeId !== MatchEventType.PENALTIES_GOAL &&
    !person
  ) {
    return null;
  }

  if (matchEventTypeId === MatchEventType.YELLOW_CARD) {
    const alreadyHasYellowCard = match.matchEvents.find((matchEvent: MatchEvent) => {
      const isYellowCard = matchEvent.matchEventTypeId === MatchEventType.YELLOW_CARD;
      const personId = person?.personInTeamId ? person.id : person?.personId;
      const isSamePlayer = matchEvent.person && matchEvent.person.id === personId;
      return isYellowCard && isSamePlayer;
    });
    if (alreadyHasYellowCard) {
      matchEventTypeId = MatchEventType.SECOND_YELLOW_CARD;
    }
  } else if (matchEventTypeId === MatchEventType.ASSIST && indirectAssist) {
    matchEventTypeId = MatchEventType.INDIRECT_ASSIST;
  } else if (matchEventTypeId === MatchEventType.GOAL && ownGoal) {
    matchEventTypeId = MatchEventType.OWN_GOAL;
  }

  if (secondaryEvents.indexOf(matchEventTypeId) > -1 || tertiaryEvents.indexOf(matchEventTypeId) > -1) {
    comment = null;
  }

  let periodId = calculatePeriod(matchEventTypeId, match.matchStatusId, penaltiesShootoutStarted);
  if (periodId === Period.HALF_TIME && substitutionEvents.includes(matchEventTypeId)) {
    periodId = Period.SECOND_HALF;
    matchEventMinutes = getPeriodTimeFromMatchLength(MatchStatus.SECOND_HALF, match.matchLength);
    matchEventSeconds = 0;
    overtime = 0;
  } else if (periodId === Period.PAUSE_BEFORE_EXTRA_TIME && substitutionEvents.includes(matchEventTypeId)) {
    periodId = Period.FIRST_HALF_EXTRA_TIME;
    matchEventMinutes = getPeriodTimeFromMatchLength(MatchStatus.FIRST_HALF_EXTRA_TIME, match.matchLength);
    matchEventSeconds = 0;
    overtime = 0;
  } else if (periodId === Period.HALF_TIME_EXTRA_TIME && substitutionEvents.includes(matchEventTypeId)) {
    periodId = Period.SECOND_HALF_EXTRA_TIME;
    matchEventMinutes = getPeriodTimeFromMatchLength(MatchStatus.SECOND_HALF_EXTRA_TIME, match.matchLength);
    matchEventSeconds = 0;
    overtime = 0;
  } else if (periodId === Period.PAUSE_BEFORE_PENALTIES_SHOOTOUT || periodId === Period.PENALTIES_SHOOTOUT) {
    const _hasExtraTime = hasExtraTime(match.matchEvents);
    matchEventMinutes = _hasExtraTime ? 120 : 90;
    matchEventSeconds = 0;
    overtime = 0;
  }

  if (periodId === Period.PENALTIES_SHOOTOUT) {
    if (matchEventTypeId === MatchEventType.PENALTY) {
      matchEventTypeId = MatchEventType.PENALTIES_PENALTY;
    }
  }

  if (payload.matchEvent) {
    matchEventMinutes = payload.matchEvent.time;
    matchEventSeconds = payload.matchEvent.seconds;
    overtime = payload.matchEvent.overtime ?? 0;
    periodId = payload.matchEvent.periodId;
  }

  const matchEvent: MatchEvent = {
    comment: comment,
    matchEventTypeId: matchEventTypeId,
    liveFeedSerialId: generateRandomId(liveFeedInternalIds),
    time: matchEventMinutes ?? null,
    seconds: matchEventSeconds ?? null,
    overtime: overtime === 0 ? null : overtime,
    matchId: match.id,
    periodId: periodId,
    liveFeedId: liveFeedId,
    important: important,
    team: team,
    person: person ? compactPersonTransformer(person) : null,
    score: undefined,
    sorting: payload.sorting ?? undefined,
    isStaffEvent: isStaffEvent,
  };

  if (
    matchEvent.matchEventTypeId === MatchEventType.ASSIST ||
    matchEvent.matchEventTypeId === MatchEventType.INDIRECT_ASSIST
  ) {
    if (payload.connectedMatchEventId) {
      matchEvent.connectedMatchEventId = payload.connectedMatchEventId;
    }
  }

  if (matchStatusEvents.includes(matchEvent.matchEventTypeId) || payload.matchEventTypeId) {
    if (matchEvent.comment) {
      matchEvent.comment = null;
    }
  }

  return matchEvent;
});

export const updateMatchEvent = createAsyncThunk('match/updateMatchEvent', (payload: any, thunkAPI) => {
  const rootState = thunkAPI.getState() as RootState;
  const inputState = rootState.input;
  const matchState = rootState.matchState;
  const { comment } = inputState.inputs;
  let { activeEventTypeId: matchEventTypeId } = inputState;
  const { selects, team: teamOption } = inputState;
  const { match } = matchState;
  const { ownGoal, indirectAssist } = inputState.checkboxes;
  const { eventTime } = inputState.inputs;

  if (payload.matchEventTypeId) {
    matchEventTypeId = payload.matchEventTypeId;
  }

  const matchEvent = lodash.clone(payload.matchEvent as MatchEvent);
  if (!payload.update) {
    matchEvent.comment = comment;
  }
  if (secondaryEvents.indexOf(matchEventTypeId) > -1 || tertiaryEvents.indexOf(matchEventTypeId) > -1) {
    matchEvent.comment = null;
  }
  matchEvent.important = importantMatchEventTypeIds.includes(matchEventTypeId) || inputState.important;

  if (matchEvent.matchEventTypeId === MatchEventType.OWN_GOAL && !ownGoal) {
    matchEvent.matchEventTypeId = MatchEventType.GOAL;
  } else if (matchEvent.matchEventTypeId === MatchEventType.GOAL && ownGoal) {
    matchEvent.matchEventTypeId = MatchEventType.OWN_GOAL;
  }
  if (matchEvent.matchEventTypeId === MatchEventType.INDIRECT_ASSIST && !indirectAssist) {
    matchEvent.matchEventTypeId = MatchEventType.ASSIST;
  } else if (matchEvent.matchEventTypeId === MatchEventType.ASSIST && indirectAssist) {
    matchEvent.matchEventTypeId = MatchEventType.INDIRECT_ASSIST;
  }

  const { team, person } = getTeamAndPerson(
    teamOption,
    match,
    matchEventTypeId,
    rootState,
    selects,
    false,
    payload.update,
  );

  if (team) {
    matchEvent.team = team;
  } else {
    matchEvent.team = null;
  }

  if (person) {
    matchEvent.person = compactPersonTransformer(person);
  } else {
    matchEvent.person = null;
  }

  let matchEventMinutes = getMinutes(eventTime);
  const matchEventSeconds = getSeconds(eventTime);
  let overtime;
  if (matchEventMinutes) {
    overtime = calculateOvertime(matchEventMinutes, match.matchLength, undefined, matchEvent.periodId);
    if (overtime) {
      if (matchEvent.periodId === Period.FIRST_HALF) {
        matchEventMinutes = getPeriodLengthFromMatchTime(match.matchLength);
      } else if (matchEvent.periodId === Period.SECOND_HALF) {
        matchEventMinutes = getPeriodLengthFromMatchTime(match.matchLength) * 2;
      } else if (matchEvent.periodId === Period.FIRST_HALF_EXTRA_TIME) {
        matchEventMinutes =
          getPeriodLengthFromMatchTime(match.matchLength) * 2 +
          getExtraTimePeriodLengthFromMatchLength(match.matchLength);
      } else if (matchEvent.periodId === Period.SECOND_HALF_EXTRA_TIME) {
        matchEventMinutes =
          getPeriodLengthFromMatchTime(match.matchLength) * 2 +
          getExtraTimePeriodLengthFromMatchLength(match.matchLength) * 2;
      }
    }
  }

  if (eventTime) {
    matchEvent.time = matchEventMinutes ?? null;
    matchEvent.seconds = matchEventSeconds ?? null;
    matchEvent.overtime = overtime === 0 ? null : overtime;
  }

  return matchEvent;
});

const getTeamAndPerson = (
  teamOption: TeamOption | null,
  match: Match,
  matchEventTypeId: number,
  rootState: RootState,
  selects: Selects,
  make?: boolean,
  update?: boolean,
) => {
  if (matchEventTypeId === MatchEventType.OFFENCE) {
    teamOption = teamOption === TeamOption.HOME ? TeamOption.AWAY : TeamOption.HOME;
  }
  let team = getCompactTeamFromTeamOption(teamOption, match);
  const teamOpposition = getCompactTeamFromTeamOption(
    teamOption === TeamOption.HOME ? TeamOption.AWAY : TeamOption.HOME,
    match,
  );

  const allPlayers = selectAllPlayers(rootState);
  const allStaff = selectAllStaff(rootState);

  let personId;
  if (matchEventTypeId === MatchEventType.FREE_KICK) {
    personId = selects.freeKick.value;
  } else if (
    matchEventTypeId === MatchEventType.YELLOW_CARD ||
    matchEventTypeId === MatchEventType.SECOND_YELLOW_CARD
  ) {
    personId = selects.yellowCard.value;
  } else if (matchEventTypeId === MatchEventType.RED_CARD) {
    personId = selects.redCard.value;
  } else if (matchEventTypeId === MatchEventType.OFFSIDE) {
    personId = selects.offside.value;
  } else if (matchEventTypeId === MatchEventType.CORNER) {
    personId = selects.corner.value;
  } else if (matchEventTypeId === MatchEventType.GOAL || matchEventTypeId === MatchEventType.OWN_GOAL) {
    personId = selects.goalScorer.value ?? selects.playerPenaltyTaker.value;
  } else if (matchEventTypeId === MatchEventType.DISALLOWED_GOAL) {
    personId = selects.goalScorer.value;
  } else if (matchEventTypeId === MatchEventType.ASSIST || matchEventTypeId === MatchEventType.INDIRECT_ASSIST) {
    personId =
      selects.assist.value ?? (matchEventTypeId === MatchEventType.INDIRECT_ASSIST ? selects.playerFouled.value : null);
  } else if (matchEventTypeId === MatchEventType.PLAYER_OFF) {
    personId = selects.playerOff.value;
  } else if (matchEventTypeId === MatchEventType.PLAYER_ON) {
    personId = selects.playerOn.value;
  } else if (
    matchEventTypeId === MatchEventType.PENALTY ||
    matchEventTypeId === MatchEventType.PENALTIES_PENALTY ||
    matchEventTypeId === MatchEventType.PENALTY_GOAL ||
    matchEventTypeId === MatchEventType.PENALTIES_GOAL
  ) {
    personId = selects.playerPenaltyTaker.value;
  } else if (matchEventTypeId === MatchEventType.FOULED) {
    personId = selects.playerFouled.value;
  } else if (matchEventTypeId === MatchEventType.OFFENCE) {
    personId = selects.playerOffence.value;
  } else if (
    (make || update) &&
    (matchEventTypeId === MatchEventType.PENALTY_MISS || matchEventTypeId === MatchEventType.PENALTIES_MISS)
  ) {
    personId = selects.playerPenaltyTaker.value;
  } else if (make && matchEventTypeId === MatchEventType.PENALTY_SAVE && teamOpposition) {
    const person = findActiveKeeper(allPlayers, teamOpposition.id);
    if (person) {
      personId = person.personId;
    }
    team = teamOpposition;
  } else if (matchEventTypeId === MatchEventType.PENALTY_SAVE && teamOpposition) {
    personId = selects.playerPenaltyKeeper.value;
    team = teamOpposition;
  }

  let person = personId ? findPersonInListFromPersonId(allPlayers, personId) : null;
  let isStaffEvent = false;
  if (!person && personId) {
    person = findPersonInListFromId(allStaff, personId);
    isStaffEvent = true;
  }

  return { team, person, isStaffEvent };
};

export const postMatchEvent = createAsyncThunk('match/postMatchEvent', async (matchEvent: MatchEvent, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const matchEventPayload = matchEventPayloadTransformer(matchEvent);
  const rootState = getState() as RootState;
  const userState = rootState.user;
  const { match } = rootState.matchState;
  const selects = rootState.input.selects;

  if (!matchEvent.internal) {
    dispatch(
      matchSliceActions.setEventInternal({
        liveFeedSerialId: matchEventPayload?.liveFeedSerialId,
        status: MatchEventStatus.POSTING,
        loading: true,
      }),
    );
  } else {
    dispatch(
      matchSliceActions.setEventInternal({
        liveFeedSerialId: matchEventPayload?.liveFeedSerialId,
        status: MatchEventStatus.RE_POSTING,
        loading: true,
      }),
    );
  }

  let updateEvent = true;

  try {
    const requestId = uuidv4();
    const request = {
      url: '/matchEvents/',
      headers: {
        'Request-Date': new Date().toUTCString(),
        userId: userState.userId,
        'X-Request-ID': requestId,
      },
      method: 'post',
      data: matchEventPayload,
    };

    storeItemToLog('Post match event', request, match.id, requestId);

    const response = await nifsAxios(request);
    if (response) {
      const resp = lodash.cloneDeep(response);
      delete resp.config;
      storeItemToLog('Post match event response', resp, matchEvent.matchId, requestId);
    }

    if (match.matchStatusId !== MatchStatus.PENALTIES) {
      if (isGoalEvent(matchEvent)) {
        dispatch(updateMatchResult());

        try {
          addOrIncrementMatchStatistics(match, matchEvent, 'shotsOnGoal', MatchStatisticType.SHOTS_ON_TARGET, dispatch);
          addOrIncrementMatchStatistics(match, matchEvent, 'totalShots', MatchStatisticType.TOTAL_SHOTS, dispatch);
          addOrIncrementMatchStatistics(match, matchEvent, 'chances', MatchStatisticType.CHANCES, dispatch);
        } catch {} //Ignore these because if they fail, the match event post will be marked as failed
      } else if (matchEvent.matchEventTypeId === MatchEventType.PENALTY_MISS) {
        addOrIncrementMatchStatistics(match, matchEvent, 'chances', MatchStatisticType.CHANCES, dispatch);
      } else if (matchEvent.matchEventTypeId === MatchEventType.PENALTY_SAVE) {
        addOrIncrementMatchStatistics(
          match,
          matchEvent,
          'shotsOnGoal',
          MatchStatisticType.SHOTS_ON_TARGET,
          dispatch,
          true,
        );
        addOrIncrementMatchStatistics(match, matchEvent, 'totalShots', MatchStatisticType.TOTAL_SHOTS, dispatch, true);
        addOrIncrementMatchStatistics(match, matchEvent, 'saves', MatchStatisticType.SAVES, dispatch, false);
      }
    } else if (matchEvent.matchEventTypeId === MatchEventType.PENALTIES_GOAL) {
      dispatch(updateMatchResult());
    }

    if (matchEvent.matchEventTypeId === MatchEventType.YELLOW_CARD) {
      addOrIncrementMatchStatistics(match, matchEvent, 'yellowCards', MatchStatisticType.YELLOW_CARDS, dispatch, false);
    } else if ([MatchEventType.RED_CARD, MatchEventType.SECOND_YELLOW_CARD].includes(matchEvent.matchEventTypeId)) {
      addOrIncrementMatchStatistics(match, matchEvent, 'redCards', MatchStatisticType.RED_CARDS, dispatch, false);
    }

    if (response.status === 201) {
      matchEvent.id = response.data.id;
      matchEvent.sorting = response.data.sorting;
      if (response.data.score) {
        matchEvent.score = response.data.score;
      }

      const updatedRootState = getState() as RootState;
      const currentMatchEvents = updatedRootState.matchState.match.matchEvents.filter(
        (mE) => mE.liveFeedSerialId === matchEvent.liveFeedSerialId,
      );

      if (currentMatchEvents && currentMatchEvents[0]) {
        const currentMatchEvent = currentMatchEvents[0];
        if (currentMatchEvent.internal && currentMatchEvent.internal.status === MatchEventStatus.RE_POSTING) {
          updateEvent = false;
        }
      }

      if (updateEvent) {
        dispatch(
          matchSliceActions.setEventInternal({
            liveFeedSerialId: matchEventPayload?.liveFeedSerialId,
            previousValue: matchEvent,
            status: MatchEventStatus.PUBLISHED,
            loading: false,
          }),
        );
      } else {
        dispatch(
          matchSliceActions.setEventInternal({
            liveFeedSerialId: matchEventPayload?.liveFeedSerialId,
            previousValue: matchEvent,
            status: MatchEventStatus.PUBLISHED,
            loading: false,
            error: false,
          }),
        );
      }
    } else {
      updateEvent = false;
      dispatch(
        matchSliceActions.setEventInternal({
          liveFeedSerialId: matchEvent.liveFeedSerialId,
          previousValue: matchEvent,
          status: MatchEventStatus.ERROR,
          loading: false,
          error: true,
        }),
      );
    }

    if (matchEvent.id && updateEvent) {
      dispatch(matchSliceActions.updateEvent(matchEvent));
    } else if (matchEvent.id && matchEvent.liveFeedSerialId) {
      dispatch(matchSliceActions.addMatchEventId({ id: matchEvent.id, liveFeedSerialId: matchEvent.liveFeedSerialId }));
    }

    return matchEvent;
  } catch (error) {
    const err = error as Error | AxiosError;
    let response;
    if (axios.isAxiosError(err) && err.response) {
      response = err.response;
    }
    if (response.data?.errorCode === ErrorCode.ASSIST_DUPLICATE) {
      dispatch(matchSliceActions.removeEvent(matchEvent));
      dispatch(inputSliceActions.setActiveSelect(selects.assist.id));
      dispatch(setActiveSelectValue(null));
      dispatch(modalsActions.setConsiderReloadModal(true));
    } else if (
      matchEvent.matchEventTypeId === MatchEventType.INJURY_TIME &&
      err.message === NetworkErrorMessage.NO_CHANGE
    ) {
      dispatch(matchSliceActions.removeEvent(matchEvent));
    } else {
      dispatch(
        matchSliceActions.setEventInternal({
          liveFeedSerialId: matchEventPayload?.liveFeedSerialId,
          status: MatchEventStatus.ERROR,
          loading: false,
          error: true,
        }),
      );
    }
    throw error;
  }
});

const addOrIncrementMatchStatistics = (
  match: Match,
  matchEvent: MatchEvent,
  name: string,
  matchStatisticsTypeId: number,
  dispatch,
  swapTeamIds: boolean = false,
) => {
  let teamId = matchEvent.team?.id;
  let otherTeamId = matchEvent.team?.id === match.homeTeam.id ? match.awayTeam.id : match.homeTeam.id;
  if (swapTeamIds && teamId) {
    const temp = teamId;
    teamId = otherTeamId;
    otherTeamId = temp;
  }

  let found = false;
  match.matchStatisticsAdmin?.forEach((matchStatisticAdmin) => {
    if (
      !found &&
      matchStatisticAdmin.matchStatisticsTypeId === matchStatisticsTypeId &&
      matchStatisticAdmin.teamId === teamId
    ) {
      found = true;
      const matchStatistic = lodash.cloneDeep(matchStatisticAdmin);
      if (matchStatistic) {
        matchStatistic.statisticsCount++;
        dispatch(patchMatchStatistics(matchStatistic));
      }
    }
  });
  if (!found && teamId) {
    const matchStatistic: MatchStatistic = {
      teamId: teamId,
      matchId: match.id,
      statisticsCount: 1,
      matchStatisticsTypeId: matchStatisticsTypeId,
      status: MatchStatisticStatus.POSTING,
    };
    dispatch(postMatchStatistics(matchStatistic));
  }

  found = false;
  match.matchStatisticsAdmin?.forEach((matchStatisticAdmin) => {
    if (
      !found &&
      matchStatisticAdmin.matchStatisticsTypeId === matchStatisticsTypeId &&
      matchStatisticAdmin.teamId === otherTeamId
    ) {
      found = true;
    }
  });
  if (!found && otherTeamId) {
    const matchStatistic: MatchStatistic = {
      teamId: otherTeamId,
      matchId: match.id,
      statisticsCount: 0,
      matchStatisticsTypeId: matchStatisticsTypeId,
      status: MatchStatisticStatus.POSTING,
    };
    dispatch(postMatchStatistics(matchStatistic));
  }
};

const decrementMatchStatistics = (
  match: Match,
  matchEvent: MatchEvent,
  name: string,
  matchStatisticsTypeId: number,
  dispatch,
  swapTeamIds: boolean = false,
) => {
  let teamId = matchEvent.team?.id;
  if (swapTeamIds && teamId) {
    teamId = teamId === match.homeTeam.id ? match.awayTeam.id : match.homeTeam.id;
  }

  match.matchStatisticsAdmin?.forEach((matchStatisticAdmin) => {
    if (matchStatisticAdmin.matchStatisticsTypeId === matchStatisticsTypeId && matchStatisticAdmin.teamId === teamId) {
      const matchStatistic = lodash.cloneDeep(matchStatisticAdmin);
      if (matchStatistic) {
        matchStatistic.statisticsCount--;
        if (matchStatistic.statisticsCount >= 0) {
          dispatch(patchMatchStatistics(matchStatistic));
        }
      }
    }
  });
};

export const patchMatchEvent = createAsyncThunk('match/patchMatchEvent', async (matchEvent: any, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const rootState = getState() as RootState;
  const userState = rootState.user;

  if (matchEvent?.internal?.previousValue) {
    const { internal, ...rest } = matchEvent;

    if (rest.overtime === undefined || rest.overtime === 0) {
      rest.overtime = null;
    }

    if (isEqualMatchEvent(internal?.previousValue, rest)) {
      console.log("match event didn't change");
      return;
    }
  }

  try {
    dispatch(
      matchSliceActions.setEventInternal({ id: matchEvent.id, status: MatchEventStatus.PATCHING, loading: true }),
    );

    delete matchEvent.sorting;
    const payload = matchEventPayloadTransformer(matchEvent);

    const requestId = uuidv4();
    const request = {
      url: `/matchEvents/${matchEvent.id}/`,
      headers: {
        'Request-Date': new Date().toUTCString(),
        userId: userState.userId,
        'X-Request-ID': requestId,
      },
      method: 'patch',
      data: payload,
    };

    storeItemToLog('Patch match event', request, matchEvent.matchId, requestId);

    const response = await nifsAxios(request);
    if (response) {
      const resp = lodash.cloneDeep(response);
      delete resp.config;
      storeItemToLog('Patch match event response', resp, matchEvent.matchId, requestId);
    }

    dispatch(
      matchSliceActions.setEventInternal({
        id: matchEvent.id,
        previousValue: matchEvent,
        status: MatchEventStatus.UPDATED,
        error: false,
        loading: false,
      }),
    );
  } catch (error) {
    let message = '';
    if (error instanceof Error) {
      message = error.message;
    }
    //catch edge case before setting it as error
    if (message === NetworkErrorMessage.NO_CHANGE) {
      dispatch(
        matchSliceActions.setEventInternal({
          id: matchEvent.id,
          status: MatchEventStatus.UPDATED,
          loading: false,
          error: false,
        }),
      );
    } else {
      dispatch(
        matchSliceActions.setEventInternal({
          id: matchEvent.id,
          status: MatchEventStatus.ERROR,
          error: true,
          loading: false,
        }),
      );
    }
  }
});

export const deleteMatchEvent = createAsyncThunk(
  'match/deleteMatchEvent',
  async (matchEvent: MatchEvent, { dispatch, getState, rejectWithValue }) => {
    const rootState = getState() as RootState;
    const userState = rootState.user;

    const matchState = rootState.matchState;
    try {
      if (matchEvent.id) {
        dispatch(
          matchSliceActions.setEventInternal({
            id: matchEvent.id,
            status: MatchEventStatus.DELETING,
            loading: true,
            error: false,
            active: false,
          }),
        );

        if (isGoalEvent(matchEvent)) {
          try {
            decrementMatchStatistics(
              matchState.match,
              matchEvent,
              'shotsOnGoal',
              MatchStatisticType.SHOTS_ON_TARGET,
              dispatch,
            );
            decrementMatchStatistics(
              matchState.match,
              matchEvent,
              'totalShots',
              MatchStatisticType.TOTAL_SHOTS,
              dispatch,
            );
            decrementMatchStatistics(matchState.match, matchEvent, 'chances', MatchStatisticType.CHANCES, dispatch);
          } catch {} //Ignore these because if they fail, the match event delete will be marked as failed
        } else if (matchEvent.matchEventTypeId === MatchEventType.PENALTY_MISS) {
          decrementMatchStatistics(matchState.match, matchEvent, 'chances', MatchStatisticType.CHANCES, dispatch);
        } else if (matchEvent.matchEventTypeId === MatchEventType.PENALTY_SAVE) {
          decrementMatchStatistics(
            matchState.match,
            matchEvent,
            'shotsOnGoal',
            MatchStatisticType.SHOTS_ON_TARGET,
            dispatch,
            true,
          );
          decrementMatchStatistics(
            matchState.match,
            matchEvent,
            'totalShots',
            MatchStatisticType.TOTAL_SHOTS,
            dispatch,
            true,
          );
          decrementMatchStatistics(matchState.match, matchEvent, 'saves', MatchStatisticType.SAVES, dispatch, false);
        }

        if (matchEvent.matchEventTypeId === MatchEventType.YELLOW_CARD) {
          decrementMatchStatistics(
            matchState.match,
            matchEvent,
            'yellowCards',
            MatchStatisticType.YELLOW_CARDS,
            dispatch,
            false,
          );
        } else if ([MatchEventType.RED_CARD, MatchEventType.SECOND_YELLOW_CARD].includes(matchEvent.matchEventTypeId)) {
          decrementMatchStatistics(
            matchState.match,
            matchEvent,
            'redCards',
            MatchStatisticType.RED_CARDS,
            dispatch,
            false,
          );
        }

        if (matchEvent.matchEventTypeId === MatchEventType.HALF_TIME) {
          dispatch(deleteHalfTimeResult());
        }

        const requestId = uuidv4();
        const request = {
          url: `/matchEvents/${matchEvent.id}/`,
          headers: {
            'Request-Date': new Date().toUTCString(),
            userId: userState.userId,
            'X-Request-ID': requestId,
          },
          method: 'delete',
        };

        storeItemToLog('Delete match event', request, matchEvent.matchId, requestId);

        const response = await nifsAxios(request);
        if (response) {
          const resp = lodash.cloneDeep(response);
          delete resp.config;
          storeItemToLog('Delete match event response', resp, matchEvent.matchId, requestId);
        }

        dispatch(matchSliceActions.removeEvent(matchEvent));
        if (isGoalEvent(matchEvent) || matchEvent.matchEventTypeId === MatchEventType.PENALTIES_GOAL) {
          dispatch(updateMatchResult());
        }
        return response.data;
      } else {
        dispatch(matchSliceActions.removeEvent(matchEvent));
      }
    } catch (err) {
      dispatch(
        matchSliceActions.setEventInternal({
          id: matchEvent.id,
          status: MatchEventStatus.DELETE_ERROR,
          error: true,
          loading: false,
        }),
      );
      return rejectWithValue(err);
    }
  },
);

export const annulMatchEvent = createAsyncThunk(
  'match/annulMatchEvent',
  async (matchEvent: MatchEvent, { getState, dispatch }) => {
    const rootState = getState() as RootState;
    const userState = rootState.user;
    const matchState = rootState.matchState;
    const { match } = matchState;
    const { id: liveFeedId } = match.liveFeeds[0];
    const { id } = matchEvent;

    await dispatch(matchSliceActions.removeGoalFromScore(matchEvent));
    await dispatch(updateMatchResult());

    const requestId = uuidv4();
    const request = {
      url: `/matchEvents/${id}/`,
      headers: {
        'Request-Date': new Date().toUTCString(),
        userId: userState.userId,
        'X-Request-ID': requestId,
      },
      method: 'patch',
      data: {
        matchEventTypeId: MatchEventType.DISALLOWED_GOAL,
        liveFeedId,
        person: matchEvent.person,
        comment: matchEvent.comment,
      },
    };

    storeItemToLog('Annul match event', request, matchEvent.matchId, requestId);

    const response = await nifsAxios(request);
    if (response) {
      const resp = lodash.cloneDeep(response);
      delete resp.config;
      storeItemToLog('Annul match event response', resp, matchEvent.matchId, requestId);
    }

    try {
      decrementMatchStatistics(
        matchState.match,
        matchEvent,
        'shotsOnGoal',
        MatchStatisticType.SHOTS_ON_TARGET,
        dispatch,
      );
      decrementMatchStatistics(matchState.match, matchEvent, 'totalShots', MatchStatisticType.TOTAL_SHOTS, dispatch);
      decrementMatchStatistics(matchState.match, matchEvent, 'chances', MatchStatisticType.CHANCES, dispatch);
    } catch {} //Ignore these because if they fail, the match event delete will be marked as failed

    return response.data;
  },
);

export const patchMatch = createAsyncThunk('match/patchMatch', async (payload: PatchMatchProps | any, thunkAPI) => {
  const { dispatch } = thunkAPI;
  const state = thunkAPI.getState() as RootState;
  const userState = state.user;
  const matchState = state.matchState;
  const { match } = matchState;

  if (matchState.showStatusDropdown) {
    dispatch(matchSliceActions.setStatusDropdown(false));
  }

  payload.patchTime = moment.tz(new Date(), 'Europe/Oslo').format();
  payload.liveFeedId = match.liveFeeds[0].id;

  const requestId = uuidv4();
  const request = {
    url: `/matches/${match.id}/?admin=1`,
    headers: {
      'Request-Date': new Date().toUTCString(),
      userId: userState.userId,
      'X-Request-ID': requestId,
    },
    method: 'patch',
    data: payload,
  };

  storeItemToLog('Patch match', request, match.id, requestId);

  const response = await nifsAxios(request);
  if (response) {
    const resp = lodash.cloneDeep(response);
    delete resp.config;
    storeItemToLog('Patch match response', resp, match.id, requestId);
  }
});

export const getMatchById = createAsyncThunk('match/getMatchById', async (id: string) => {
  const response = await nifsAxios.get(`/matches/${id}/?admin=1`);
  return response.data;
});

export const postMatchStatistics = createAsyncThunk(
  'match/postMatchStatistics',
  async (matchStatistic: MatchStatistic, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const rootState = getState() as RootState;
    const userState = rootState.user;
    const match = rootState.matchState.match;

    const matchStatisticPayload = matchStatisticPayloadTransformer(matchStatistic);

    const requestId = uuidv4();
    const request = {
      url: 'matchStatistics/',
      headers: {
        'Request-Date': new Date().toUTCString(),
        userId: userState.userId,
        'X-Request-ID': requestId,
      },
      method: 'post',
      data: matchStatisticPayload,
    };

    dispatch(matchSliceActions.addMatchStatistic(matchStatistic));

    storeItemToLog('Post match statistics', request, match.id, requestId);

    const response = await nifsAxios(request);
    if (response) {
      const resp = lodash.cloneDeep(response);
      delete resp.config;
      storeItemToLog('Post match statistics response', resp, match.id, requestId);
    }

    if (response.status === 201) {
      dispatch(
        matchSliceActions.updateMatchStatistic({
          id: response.data,
          status: MatchStatisticStatus.OK,
          teamId: matchStatistic.teamId,
          matchStatisticTypeId: matchStatistic.matchStatisticsTypeId,
          statisticsCount: matchStatistic.statisticsCount,
        }),
      );
    }
  },
);

export const patchMatchStatistics = createAsyncThunk(
  'match/patchMatchStatistics',
  async (matchStatistic: MatchStatistic, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const rootState = getState() as RootState;
    const userState = rootState.user;
    const match = rootState.matchState.match;

    dispatch(
      matchSliceActions.updateMatchStatistic({
        id: matchStatistic.id,
        status: MatchStatisticStatus.PATCHING,
        teamId: matchStatistic.teamId,
        matchStatisticTypeId: matchStatistic.matchStatisticsTypeId,
        statisticsCount: matchStatistic.statisticsCount,
      }),
    );

    const requestId = uuidv4();
    const request = {
      url: `/matchStatistics/${matchStatistic.id}/`,
      headers: {
        'Request-Date': new Date().toUTCString(),
        userId: userState.userId,
        'X-Request-ID': requestId,
      },
      method: 'patch',
      data: {
        statisticsCount: matchStatistic.statisticsCount,
      },
    };

    storeItemToLog('Patch match statistics', request, match.id, requestId);

    const response = await nifsAxios(request);
    if (response) {
      const resp = lodash.cloneDeep(response);
      delete resp.config;
      storeItemToLog('Patch match statistics response', resp, match.id, requestId);
    }

    if (response.status === 204) {
      dispatch(
        matchSliceActions.updateMatchStatistic({
          id: matchStatistic.id,
          status: MatchStatisticStatus.OK,
          teamId: matchStatistic.teamId,
          matchStatisticTypeId: matchStatistic.matchStatisticsTypeId,
          statisticsCount: matchStatistic.statisticsCount,
        }),
      );
    }
  },
);

/*
 * Action creators
 */
export const updateMatchResult = createAsyncThunk(
  'match/updateMatchResult',
  async (newScore: MatchScore | undefined | null, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;

    const rootState = getState() as RootState;
    const { match } = rootState.matchState;
    const { matchStatusId } = match;

    let payload: any;

    if (newScore && !validateNewScore()) {
      return;
    }

    switch (matchStatusId) {
      case MatchStatus.HALF_TIME:
        payload = getMatchResultPayloadHalfTime();
        break;

      case MatchStatus.HALF_TIME_EXTRA_TIME:
        payload = getMatchResultPayloadHalfTimeExtraTime();
        break;

      case MatchStatus.FIRST_HALF_EXTRA_TIME:
      case MatchStatus.SECOND_HALF_EXTRA_TIME:
        payload = getMatchResultPayloadExtraTime();
        break;

      case MatchStatus.PENALTIES:
        payload = getMatchResultPayloadPenalties();
        break;

      default:
        payload = getMatchResultPayloadDefault();
        break;
    }

    dispatch(
      patchMatch({
        result: payload,
      }),
    );

    function validateNewScore() {
      if (newScore) {
        return !(newScore.home < 0 || newScore.away < 0);
      }
    }

    function getMatchResultPayloadHalfTime() {
      if (newScore) {
        return {
          homeScore45: newScore.home,
          awayScore45: newScore.away,
          homeScore90: newScore.home,
          awayScore90: newScore.away,
        };
      }
    }

    function getMatchResultPayloadDefault() {
      if (newScore) {
        return {
          homeScore90: newScore.home,
          awayScore90: newScore.away,
        };
      } else {
        return {
          homeScore90: match.result?.homeScore90,
          awayScore90: match.result?.awayScore90,
        };
      }
    }

    function getMatchResultPayloadHalfTimeExtraTime() {
      if (newScore) {
        return {
          homeScore105: newScore.home,
          awayScore105: newScore.away,
        };
      }
    }

    function getMatchResultPayloadExtraTime() {
      if (newScore) {
        return {
          homeScore120: newScore.home,
          awayScore120: newScore.away,
        };
      } else {
        return {
          homeScore120: match.result?.homeScore120,
          awayScore120: match.result?.awayScore120,
        };
      }
    }

    function getMatchResultPayloadPenalties() {
      if (newScore) {
        return {
          homeScorePenalties: newScore.home,
          awayScorePenalties: newScore.away,
        };
      } else {
        return {
          homeScorePenalties: match.result?.homeScorePenalties,
          awayScorePenalties: match.result?.awayScorePenalties,
        };
      }
    }
  },
);

export const deleteHalfTimeResult = createAsyncThunk('match/deleteHalfTimeResult', async (payload, thunkAPI) => {
  const { dispatch } = thunkAPI;

  dispatch(
    patchMatch({
      result: { homeScore45: null, awayScore45: null },
    }),
  );
});

export const setMatchStatusToNotStarted = createAsyncThunk(
  'match/setMatchStatusId',
  async (payload: any, { dispatch }) => {
    return dispatch(
      patchMatch({
        matchStatusId: MatchStatus.NOT_STARTED,
        result: {
          homeScore45: null,
          awayScore45: null,
          homeScore90: null,
          awayScore90: null,
          homeScore105: null,
          awayScore105: null,
          homeScore120: null,
          awayScore120: null,
        },
      }),
    );
  },
);

export const startFirstHalf = createAsyncThunk('match/startFirstHalf', async (payload: any, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const rootState = getState() as RootState;
  const { match } = rootState.matchState;

  const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

  dispatch(
    patchMatch({
      matchStatusId: MatchStatus.FIRST_HALF,
      result: {
        homeScore90: 0,
        awayScore90: 0,
      },
    }),
  );

  await addPeriodEvent(
    match,
    MatchEventType.MATCH_KICKS_OFF,
    Period.BEFORE_MATCH,
    0,
    0,
    null,
    dispatch,
    liveFeedInternalIds,
  );
});

export const startHalfTime = createAsyncThunk('match/startHalfTime', async (payload: any, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const rootState = getState() as RootState;
  const { match } = rootState.matchState;
  const { time } = rootState.timeState;

  const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

  dispatch(
    patchMatch({
      matchStatusId: MatchStatus.HALF_TIME,
      result: {
        homeScore45: match.result?.homeScore90,
        awayScore45: match.result?.awayScore90,
      },
    }),
  );

  let matchEventMinutes = time.minutes;
  const overtime = calculateOvertime(time.minutes, match.matchLength, match.matchStatusId);
  if (overtime) {
    matchEventMinutes = getEventTimeWhenOvertime(overtime, match, matchEventMinutes);
  }
  await addPeriodEvent(
    match,
    MatchEventType.HALF_TIME,
    Period.FIRST_HALF,
    matchEventMinutes,
    time.seconds,
    overtime,
    dispatch,
    liveFeedInternalIds,
  );
});

export const startSecondHalf = createAsyncThunk('match/startSecondHalf', async (payload: any, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const rootState = getState() as RootState;
  const { match } = rootState.matchState;

  const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

  dispatch(
    patchMatch({
      matchStatusId: MatchStatus.SECOND_HALF,
    }),
  );

  await addPeriodEvent(
    match,
    MatchEventType.SECOND_HALF,
    Period.HALF_TIME,
    getPeriodLengthFromMatchTime(match.matchLength),
    0,
    null,
    dispatch,
    liveFeedInternalIds,
  );
});

export const startFirstHalfExtraTime = createAsyncThunk(
  'match/startFirstHalfExtraTime',
  async (payload: any, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const rootState = getState() as RootState;
    const { match } = rootState.matchState;

    const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

    dispatch(
      patchMatch({
        matchStatusId: MatchStatus.FIRST_HALF_EXTRA_TIME,
        result: {
          homeScore120: match.result?.homeScore90,
          awayScore120: match.result?.awayScore90,
        },
      }),
    );

    await addPeriodEvent(
      match,
      MatchEventType.FIRST_HALF_EXTRA_TIME,
      Period.PAUSE_BEFORE_EXTRA_TIME,
      getPeriodLengthFromMatchTime(match.matchLength) * 2,
      0,
      null,
      dispatch,
      liveFeedInternalIds,
    );
  },
);

export const halfTimeExtraTime = createAsyncThunk('match/halfTimeExtraTime', async (payload: any, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const rootState = getState() as RootState;
  const { match } = rootState.matchState;
  const { time } = rootState.timeState;

  const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

  dispatch(
    patchMatch({
      matchStatusId: MatchStatus.HALF_TIME_EXTRA_TIME,
      result: {
        homeScore105: match.result?.homeScore120,
        awayScore105: match.result?.awayScore120,
      },
    }),
  );

  let matchEventMinutes = time.minutes;
  const overtime = calculateOvertime(time.minutes, match.matchLength, match.matchStatusId);
  if (overtime) {
    matchEventMinutes = getEventTimeWhenOvertime(overtime, match, matchEventMinutes);
  }

  await addPeriodEvent(
    match,
    MatchEventType.HALF_TIME_EXTRA_TIME,
    Period.FIRST_HALF_EXTRA_TIME,
    matchEventMinutes,
    time.seconds,
    overtime,
    dispatch,
    liveFeedInternalIds,
  );
});

export const startSecondHalfExtraTime = createAsyncThunk(
  'match/startSecondHalfExtraTime',
  async (payload: any, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const rootState = getState() as RootState;
    const { match } = rootState.matchState;

    const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

    dispatch(
      patchMatch({
        matchStatusId: MatchStatus.SECOND_HALF_EXTRA_TIME,
      }),
    );

    await addPeriodEvent(
      match,
      MatchEventType.SECOND_HALF_EXTRA_TIME,
      Period.HALF_TIME_EXTRA_TIME,
      getPeriodLengthFromMatchTime(match.matchLength) * 2 + getExtraTimePeriodLengthFromMatchLength(match.matchLength),
      0,
      null,
      dispatch,
      liveFeedInternalIds,
    );
  },
);

export const endMatch90 = createAsyncThunk('match/endMatch90', async (payload: any, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const rootState = getState() as RootState;
  const { match } = rootState.matchState;
  const { time } = rootState.timeState;

  const _hasPenaltiesShootoutStarted = hasPenaltiesShootoutStarted(match.matchEvents);
  const _hasExtraTime = hasExtraTime(match.matchEvents);
  const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

  dispatch(
    patchMatch({
      matchStatusId: MatchStatus.PLAYED,
    }),
  );

  let matchEventMinutes = time.minutes;
  const overtime = calculateOvertime(time.minutes, match.matchLength, match.matchStatusId);
  if (overtime) {
    matchEventMinutes = getEventTimeWhenOvertime(overtime, match, matchEventMinutes);
  }
  const periodId = calculatePeriod(MatchEventType.MATCH_ENDS, match.matchStatusId, _hasPenaltiesShootoutStarted);
  if (_hasPenaltiesShootoutStarted && _hasExtraTime) {
    matchEventMinutes = getPeriodTimeFromMatchLength(MatchStatus.PENALTIES, match.matchLength);
  } else if (_hasPenaltiesShootoutStarted) {
    matchEventMinutes = getPeriodTimeFromMatchLength(MatchStatus.FIRST_HALF_EXTRA_TIME, match.matchLength);
  }
  await addPeriodEvent(
    match,
    MatchEventType.MATCH_ENDS,
    periodId,
    matchEventMinutes,
    time.seconds,
    overtime,
    dispatch,
    liveFeedInternalIds,
  );
});

export const goToExtraTime = createAsyncThunk('match/goToExtraTime', async (payload: any, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const rootState = getState() as RootState;
  const { match } = rootState.matchState;
  const { time } = rootState.timeState;

  const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

  dispatch(
    patchMatch({
      matchStatusId: MatchStatus.PAUSE_BEFORE_EXTRA_TIME,
    }),
  );

  let matchEventMinutes = time.minutes;
  const overtime = calculateOvertime(time.minutes, match.matchLength, match.matchStatusId);
  if (overtime) {
    matchEventMinutes = getEventTimeWhenOvertime(overtime, match, matchEventMinutes);
  }
  await addPeriodEvent(
    match,
    MatchEventType.SECOND_HALF_ENDED,
    Period.SECOND_HALF,
    matchEventMinutes,
    time.seconds,
    overtime,
    dispatch,
    liveFeedInternalIds,
  );
});

export const goToPauseBeforePenalties = createAsyncThunk(
  'match/goToPauseBeforePenalties',
  async (payload: any, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const rootState = getState() as RootState;
    const { match } = rootState.matchState;
    const { time } = rootState.timeState;

    const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

    dispatch(
      patchMatch({
        matchStatusId: MatchStatus.PENALTIES,
        result: {
          homeScorePenalties: 0,
          awayScorePenalties: 0,
        },
      }),
    );

    let matchEventTypeId = MatchEventType.EXTRA_TIME_ENDED_MATCH_GOES_TO_PENALTIES;
    let period = Period.SECOND_HALF_EXTRA_TIME;
    if (match.matchStatusId === MatchStatus.SECOND_HALF) {
      matchEventTypeId = MatchEventType.SECOND_HALF_ENDED;
      period = Period.SECOND_HALF;
    }

    let matchEventMinutes = time.minutes;
    const overtime = calculateOvertime(time.minutes, match.matchLength, match.matchStatusId);
    if (overtime) {
      matchEventMinutes = getEventTimeWhenOvertime(overtime, match, matchEventMinutes);
    }
    await addPeriodEvent(
      match,
      matchEventTypeId,
      period,
      matchEventMinutes,
      time.seconds,
      overtime,
      dispatch,
      liveFeedInternalIds,
    );
  },
);

export const goToPenalties = createAsyncThunk('match/goToPenalties', async (payload: any, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const rootState = getState() as RootState;
  const { match } = rootState.matchState;

  const liveFeedInternalIds = populateLiveFeedSerialIds(match.matchEvents);

  const _hasExtraTime = hasExtraTime(match.matchEvents);

  await addPeriodEvent(
    match,
    MatchEventType.PENALTIES,
    Period.PAUSE_BEFORE_PENALTIES_SHOOTOUT,
    _hasExtraTime ? 120 : 90,
    0,
    0,
    dispatch,
    liveFeedInternalIds,
  );
});

async function addPeriodEvent(
  match: Match,
  matchEventTypeId: number,
  periodId: number,
  time: number,
  seconds: number,
  overtime: number | null,
  dispatch,
  liveFeedSerialIds: Array<number>,
) {
  if (!eventAlreadyExists(match.matchEvents, matchEventTypeId)) {
    createPeriodEvent(liveFeedSerialIds).then();
  } else {
    if (!findFirstEvent(match.matchEvents, matchEventTypeId)) {
      createPeriodEvent(liveFeedSerialIds).then();
    }
  }

  async function createPeriodEvent(liveFeedSerialIds: Array<number>) {
    const timestamp = moment.tz(new Date(), 'Europe/Oslo').format();

    let matchEvent: MatchEvent = {
      comment: '',
      matchEventTypeId: matchEventTypeId,
      liveFeedSerialId: generateRandomId(liveFeedSerialIds),
      time: time,
      seconds: seconds,
      overtime: overtime === 0 ? null : overtime,
      matchId: match.id,
      periodId: periodId,
      liveFeedId: match.liveFeeds[0].id,
      important: true,
      team: null,
      person: null,
      score: undefined,
      sorting: undefined,
      timestamp: timestamp,
    };

    dispatch(matchSliceActions.addEvent(matchEvent));
    const postResponse = await dispatch(postMatchEvent(matchEvent));
    matchEvent = postResponse.payload as MatchEvent;
    if (matchEvent && matchEvent.id) {
      dispatch(matchSliceActions.updateEvent(matchEvent));
    }
  }
}

export const resetToBeforeMatch = (): AppThunk => async (dispatch: ThunkDispatch<any, any, any>, getState) => {
  const { match } = getState().matchState;

  dispatch(timeSliceActions.resetMatchTime(match.id));
  dispatch(setMatchStatusToNotStarted({}));

  const matchKicksOffEvent = findFirstEvent(match.matchEvents, MatchEventType.MATCH_KICKS_OFF);

  if (matchKicksOffEvent) {
    dispatch(deleteMatchEvent(matchKicksOffEvent));
  }
};
